import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Film, BarChart3, Sparkles, Target, Lightbulb,
  Users, Settings, Crown, LogOut, Zap, GitCompareArrows,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { UsageMeter } from "@/components/UsageMeter";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const SECTIONS = [
  {
    label: "Workspace",
    items: [
      { label: "Overview", icon: LayoutDashboard, path: "/dashboard" },
      { label: "Video Library", icon: Film, path: "/library" },
      { label: "Analytics", icon: BarChart3, path: "/analytics" },
      { label: "Compare", icon: GitCompareArrows, path: "/compare" },
      { label: "AI Insights", icon: Sparkles, path: "/ai-insights" },
    ],
  },
  {
    label: "Tools",
    items: [
      { label: "Competitors", icon: Target, path: "/competitors" },
      { label: "Idea Lab", icon: Lightbulb, path: "/ideas" },
      { label: "Team", icon: Users, path: "/team" },
    ],
  },
  {
    label: "Account",
    items: [{ label: "Settings", icon: Settings, path: "/settings" }],
  },
];

export function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const { openPremium } = useUI();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <aside className="flex h-full w-64 flex-col border-r border-border bg-card">
      <div className="flex items-center gap-2 px-5 h-16 border-b border-border">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground neon-primary">
          <Zap className="h-4 w-4" />
        </span>
        <span className="font-display text-lg font-bold tracking-tight">
          Video<span className="gradient-text">Lens</span>
        </span>
      </div>

      <nav className="flex-1 overflow-y-auto no-scrollbar px-3 py-4 space-y-6">
        {SECTIONS.map((section) => (
          <div key={section.label}>
            <p className="overline text-muted-foreground px-3 mb-2">{section.label}</p>
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={onNavigate}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                    }`
                  }
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-border p-3 space-y-3">
        <div className="rounded-xl border border-border p-3">
          <UsageMeter />
          {user?.plan !== "plus" && (
            <Button
              onClick={openPremium}
              size="sm"
              className="w-full mt-3 gap-1.5 bg-amber-500 hover:bg-amber-500/90 text-white"
              data-testid="sidebar-upgrade-btn"
            >
              <Crown className="h-3.5 w-3.5" />
              {user?.plan === "pro" ? "Upgrade to Plus" : "Upgrade plan"}
            </Button>
          )}
        </div>

        <div className="flex items-center gap-3 px-1">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-secondary text-xs">
              {(user?.name || "U").slice(0, 1).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              {user?.plan === "plus" && <Crown className="h-3 w-3 text-amber-500 shrink-0" />}
            </div>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
          <button
            onClick={doLogout}
            className="text-muted-foreground hover:text-foreground p-1.5 rounded-md hover:bg-secondary"
            data-testid="logout-btn"
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
